import { z } from 'zod';

import type { Theme } from '@/contexts/AppContext';
import type { CoreThemeColors, ThemeConfig, ThemesConfig } from '@/themes';

// ─── Theme Schemas ───────────────────────────────────────────────────

export const ThemeSchema = z.enum(['dark', 'light', 'system', 'custom']) satisfies z.ZodType<Theme>;

const HslValue = z.string().regex(/^\d/);

export const CoreThemeColorsSchema = z.object({
  background: HslValue,
  text: HslValue,
  primary: HslValue,
}) satisfies z.ZodType<CoreThemeColors>;

export const LegacyThemeTokensSchema = z.object({
  background: HslValue,
  foreground: HslValue,
  primary: HslValue,
}).passthrough();

export const LegacyFourColorSchema = z.object({
  background: HslValue,
  text: HslValue,
  primary: HslValue,
  secondary: HslValue,
}).transform(({ background, text, primary }): CoreThemeColors => ({
  background,
  text,
  primary,
}));

export const ThemeColorsCompatSchema = z.union([
  CoreThemeColorsSchema,
  LegacyFourColorSchema,
  LegacyThemeTokensSchema.transform((legacy): CoreThemeColors => ({
    background: legacy.background,
    text: legacy.foreground,
    primary: legacy.primary,
  })),
]);

export const ThemeFontSchema = z.object({
  family: z.string(),
  url: z.url().optional(),
});

export const ThemeBackgroundSchema = z.object({
  url: z.url(),
  mode: z.enum(['cover', 'tile']).optional(),
  dimensions: z.string().optional(),
  mimeType: z.string().optional(),
  blurhash: z.string().optional(),
});

export const ThemeConfigSchema = z.object({
  title: z.string().optional(),
  colors: CoreThemeColorsSchema,
  font: ThemeFontSchema.optional(),
  titleFont: ThemeFontSchema.optional(),
  background: ThemeBackgroundSchema.optional(),
});

export const ThemesConfigSchema = z.object({
  light: z.lazy(() => ThemeConfigSchema),
  dark: z.lazy(() => ThemeConfigSchema),
}) satisfies z.ZodType<ThemesConfig>;

export const ThemeConfigCompatSchema = z.union([
  ThemeConfigSchema,
  ThemeColorsCompatSchema.transform((colors): ThemeConfig => ({ colors })),
]);

// ─── Relay Schema ────────────────────────────────────────────────────

export const RelayMetadataSchema = z.object({
  relays: z.array(z.object({
    // wss:// is valid; keep loose string so older/custom entries still load
    url: z.string().min(1),
    read: z.boolean(),
    write: z.boolean(),
  })),
  updatedAt: z.number(),
});

// ─── AI Provider Schemas ─────────────────────────────────────────────

export const AIModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  providerId: z.string(),
});

export const AIProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  baseUrl: z.string().url(),
  apiKey: z.string(),
  useCorsProxy: z.boolean(),
  models: z.array(AIModelSchema).default([]),
  modelsFetchedAt: z.number().optional(),
});

// ─── AppConfigSchema ─────────────────────────────────────────────────

export const AppConfigSchema = z.object({
  appName: z.string().optional(),
  appId: z.string().optional(),
  shareOrigin: z.string().url().optional(),
  theme: ThemeSchema,
  customTheme: ThemeConfigCompatSchema.optional(),
  themes: ThemesConfigSchema.optional(),
  relayMetadata: RelayMetadataSchema,
  blossomServers: z.array(z.string()).optional().default([]),
  networkListsPubkey: z.string().optional(),
  // Legacy flags (ignored at runtime; accepted so old configs don't drop)
  useAppRelays: z.boolean().optional(),
  useUserRelays: z.boolean().optional(),
  corsProxy: z.string(),
  aiProviders: z.array(AIProviderSchema).optional().default([]),
  defaultAiProvider: z.string().optional(),
});
